import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../config";
import { CATEGORY_LABELS, RANK_LABELS, SPECIAL_JURY_LABEL } from "../utils/jeedigunta";
import { FaArrowLeft, FaSave, FaCheck } from "react-icons/fa";

// Password-gated editor for jeedigunta_results data gaps.
// Pairs with db/jeedigunta-admin-rpcs.sql.
//   • jeedigunta_admin_list_results(p_password, p_season_slug) → rows
//   • jeedigunta_admin_update_result(p_password, p_result_id, ...) → ack
// Same kk26 password as the trophy report.

const PASSWORD_STORAGE_KEY = "jkkp_admin_pw";

const JeedigntaResultsAdmin = () => {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(PASSWORD_STORAGE_KEY) || ""
  );
  const [pwInput, setPwInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [seasonFilter, setSeasonFilter] = useState("2024");
  const [edits, setEdits] = useState({}); // result_id → { field: value, ... }
  const [savingId, setSavingId] = useState(null);
  const [savedFlash, setSavedFlash] = useState({}); // result_id → ts

  useEffect(() => {
    document.title = "Jeedigunta Results Admin";
  }, []);

  useEffect(() => {
    if (!password) return;
    setLoading(true);
    setError("");
    (async () => {
      const { data, error: err } = await supabase.rpc(
        "jeedigunta_admin_list_results",
        { p_password: password, p_season_slug: seasonFilter || null }
      );
      if (err) {
        setError(err.message?.includes("unauthorized") ? "Wrong password." : err.message);
        sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
        setPassword("");
        setRows([]);
        setLoading(false);
        return;
      }
      sessionStorage.setItem(PASSWORD_STORAGE_KEY, password);
      setRows(data || []);
      setLoading(false);
    })();
  }, [password, seasonFilter]);

  const grouped = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      if (!out[r.category]) out[r.category] = [];
      out[r.category].push(r);
    });
    return out;
  }, [rows]);

  const setEdit = (id, field, value) => {
    setEdits((e) => ({ ...e, [id]: { ...e[id], [field]: value } }));
  };

  const saveRow = async (r) => {
    const e = edits[r.result_id] || {};
    if (Object.keys(e).length === 0) return;
    setSavingId(r.result_id);
    setError("");
    const { error: err } = await supabase.rpc(
      "jeedigunta_admin_update_result",
      {
        p_password: password,
        p_result_id: r.result_id,
        p_storyteller_name_te:
          e.storyteller_name_te !== undefined ? e.storyteller_name_te : null,
        p_story_name_te:
          e.story_name_te !== undefined ? e.story_name_te : null,
        p_writer_name_te:
          e.writer_name_te !== undefined ? e.writer_name_te : null,
        p_performance_id:
          e.performance_id !== undefined && e.performance_id !== ""
            ? Number(e.performance_id)
            : null,
        p_clear_performance_id: e.performance_id === "",
      }
    );
    setSavingId(null);
    if (err) {
      setError(err.message);
      return;
    }
    // Update local state
    setRows((rs) =>
      rs.map((row) =>
        row.result_id === r.result_id
          ? {
              ...row,
              storyteller_name_te:
                e.storyteller_name_te !== undefined
                  ? e.storyteller_name_te || null
                  : row.storyteller_name_te,
              story_name_te:
                e.story_name_te !== undefined
                  ? e.story_name_te || null
                  : row.story_name_te,
              writer_name_te:
                e.writer_name_te !== undefined
                  ? e.writer_name_te || null
                  : row.writer_name_te,
              performance_id:
                e.performance_id !== undefined
                  ? e.performance_id === ""
                    ? null
                    : Number(e.performance_id)
                  : row.performance_id,
            }
          : row
      )
    );
    setEdits((es) => {
      const next = { ...es };
      delete next[r.result_id];
      return next;
    });
    setSavedFlash((f) => ({ ...f, [r.result_id]: Date.now() }));
    setTimeout(
      () => setSavedFlash((f) => {
        const n = { ...f };
        delete n[r.result_id];
        return n;
      }),
      2000
    );
  };

  // ── Password gate ──────────────────────────────────────────────
  if (!password) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPassword(pwInput);
          }}
          className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-sm"
        >
          <h1 className="text-xl font-bold text-amber-300 mb-2">
            Jeedigunta Results — Admin
          </h1>
          <p className="text-sm text-slate-400 mb-4">
            Edit data gaps in jeedigunta_results.
          </p>
          <input
            type="password"
            autoFocus
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Password"
            className="w-full bg-slate-900 border border-slate-600 focus:border-amber-400 rounded px-3 py-2 text-slate-100 outline-none"
          />
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
          <button
            type="submit"
            className="mt-4 w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-2 rounded transition-colors"
          >
            Unlock
          </button>
          <Link
            to="/jeedigunta"
            className="mt-3 inline-flex items-center gap-1 text-slate-400 hover:text-amber-300 text-xs"
          >
            <FaArrowLeft /> Back to public site
          </Link>
        </form>
      </div>
    );
  }

  // ── Editor ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-amber-300">
              Jeedigunta Results — Admin
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Fill in storyteller / story / writer Telugu names. Optional:
              link a row to a performance_id from the <code>performances</code>{" "}
              table to pull joined data.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={seasonFilter}
              onChange={(e) => setSeasonFilter(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm"
            >
              <option value="2024">2024</option>
              <option value="2026">2026</option>
              <option value="">All seasons</option>
            </select>
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(PASSWORD_STORAGE_KEY);
                setPassword("");
              }}
              className="text-xs text-slate-400 hover:text-amber-300 underline-offset-4 hover:underline"
            >
              Lock
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/40 border border-red-600/40 text-red-200 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-slate-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-slate-400">
            No results in this season. (Are RPCs deployed?)
          </p>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([cat, catRows]) => {
              const lbl = CATEGORY_LABELS[cat] || { te: cat, en: cat };
              return (
                <section
                  key={cat}
                  className="bg-slate-800/40 border border-slate-700 rounded-xl p-4"
                >
                  <h2 className="text-lg font-semibold text-amber-300 mb-3">
                    {lbl.te}{" "}
                    <span className="text-slate-400 italic font-normal text-sm">
                      · {lbl.en}
                    </span>
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-separate border-spacing-y-1">
                      <thead className="text-slate-400 text-xs uppercase">
                        <tr>
                          <th className="text-left px-2 py-1 w-32">Award</th>
                          <th className="text-left px-2 py-1">Storyteller</th>
                          <th className="text-left px-2 py-1">Story</th>
                          <th className="text-left px-2 py-1">Writer</th>
                          <th className="text-left px-2 py-1 w-24">Perf ID</th>
                          <th className="text-right px-2 py-1 w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {catRows.map((r) => {
                          const e = edits[r.result_id] || {};
                          const dirty = Object.keys(e).length > 0;
                          const saved = savedFlash[r.result_id];
                          const award = r.is_special_jury
                            ? `⭐ ${SPECIAL_JURY_LABEL.te}${
                                r.joint_group != null ? ` (joint ${r.joint_group})` : ""
                              }`
                            : `${RANK_LABELS[r.rank]?.te || `Rank ${r.rank}`}`;
                          return (
                            <tr
                              key={r.result_id}
                              className="bg-slate-800 hover:bg-slate-800/70 align-top"
                            >
                              <td className="px-2 py-2 rounded-l text-slate-300">
                                {award}
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  #{r.result_id}
                                </div>
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  defaultValue={r.storyteller_name_te || ""}
                                  onChange={(ev) =>
                                    setEdit(
                                      r.result_id,
                                      "storyteller_name_te",
                                      ev.target.value
                                    )
                                  }
                                  className="w-full bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-slate-100 outline-none focus:border-amber-400"
                                  placeholder="కథకుడు / కథకురాలు"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  defaultValue={r.story_name_te || ""}
                                  onChange={(ev) =>
                                    setEdit(
                                      r.result_id,
                                      "story_name_te",
                                      ev.target.value
                                    )
                                  }
                                  className="w-full bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-slate-100 outline-none focus:border-amber-400 italic"
                                  placeholder="కథ"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  defaultValue={r.writer_name_te || ""}
                                  onChange={(ev) =>
                                    setEdit(
                                      r.result_id,
                                      "writer_name_te",
                                      ev.target.value
                                    )
                                  }
                                  className="w-full bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-slate-100 outline-none focus:border-amber-400"
                                  placeholder="రచన"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  defaultValue={r.performance_id || ""}
                                  onChange={(ev) =>
                                    setEdit(
                                      r.result_id,
                                      "performance_id",
                                      ev.target.value
                                    )
                                  }
                                  className="w-full bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-slate-100 outline-none focus:border-amber-400 font-mono text-xs"
                                  placeholder="(none)"
                                />
                              </td>
                              <td className="px-2 py-2 rounded-r text-right">
                                {saved ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                                    <FaCheck /> Saved
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => saveRow(r)}
                                    disabled={!dirty || savingId === r.result_id}
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-semibold transition-colors ${
                                      dirty
                                        ? "bg-amber-500 hover:bg-amber-400 text-slate-900"
                                        : "bg-slate-700 text-slate-500 cursor-not-allowed"
                                    }`}
                                  >
                                    <FaSave />{" "}
                                    {savingId === r.result_id ? "Saving…" : "Save"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-700 text-sm text-slate-400">
          <Link to="/manage" className="hover:text-amber-300">
            ← Back to Manage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default JeedigntaResultsAdmin;
